import {
  Text,
  Card,
  Table,
  ActionIcon,
  Menu,
  Tooltip,
} from '@mantine/core';
import { Settings, MoreVertical, Send, Mail } from 'react-feather';
import type { Coach } from '../../../types';
import { ColumnSettingsPopover, Pagination, type SortConfig } from '../../../components/common';
import type { ColumnConfig } from '../../../hooks';
import { renderColumnHeader, renderCellContent } from './CoachCellRenderers';
import classes from './CoachesListView.module.css';

export interface CoachesListViewProps {
  coaches: Coach[];
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  visibleColumnCount: number;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onCoachClick: (coach: Coach) => void;
  onSendInvitation: (coach: Coach, e: React.MouseEvent) => void;
  sendingInvitation: string | null;
}

export function CoachesListView({
  coaches,
  columns,
  onColumnsChange,
  sortConfig,
  onSort,
  visibleColumnCount,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onCoachClick,
  onSendInvitation,
  sendingInvitation,
}: CoachesListViewProps) {
  return (
    <Card withBorder p={0} styles={{ root: { boxShadow: 'none' } }}>
      <Table.ScrollContainer minWidth={1000}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              {columns
                .filter((col) => col.visible)
                .map((col) => renderColumnHeader(col, sortConfig, onSort, classes.tableHeader))}
              {/* Settings Gear Column */}
              <Table.Th className={classes.settingsColumn}>
                <ColumnSettingsPopover
                  columns={columns}
                  onColumnsChange={onColumnsChange}
                  trigger={
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <Settings size={16} />
                    </ActionIcon>
                  }
                />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {coaches.map((coach) => (
              <Table.Tr
                key={coach.id}
                className={classes.tableRow}
                onClick={() => onCoachClick(coach)}
              >
                {columns
                  .filter((col) => col.visible)
                  .map((col) => (
                    <Table.Td key={col.id}>
                      {renderCellContent(col, coach)}
                    </Table.Td>
                  ))}
                {/* Actions column */}
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Menu shadow="md" width={200} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        loading={sendingInvitation === coach.id}
                      >
                        <MoreVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Actions</Menu.Label>
                      {coach.email || coach.preferred_email ? (
                        <Menu.Item
                          leftSection={coach.magic_link_invitation_sent_at ? <Send size={14} /> : <Mail size={14} />}
                          onClick={(e) => onSendInvitation(coach, e)}
                          disabled={sendingInvitation === coach.id}
                        >
                          {coach.magic_link_invitation_sent_at ? 'Resend Invitation' : 'Send Invitation'}
                        </Menu.Item>
                      ) : (
                        <Tooltip label="Coach does not have an email address" position="left">
                          <Menu.Item
                            leftSection={<Mail size={14} />}
                            disabled
                            style={{ opacity: 0.5 }}
                          >
                            Send Invitation
                          </Menu.Item>
                        </Tooltip>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
            {coaches.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={visibleColumnCount + 1}>
                  <Text ta="center" c="dimmed" py="xl">
                    No coaches found matching your filters
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </Card>
  );
}
